import { useState, useEffect, useCallback } from "react";

type IdleState = {
  execute: () => void;
  status: "idle";
  data: null;
  error: null;
  isLoading: true;
};

type PendingState = {
  execute: () => void;
  status: "pending";
  data: null;
  error: null;
  isLoading: true;
};

type ResolvedState<T> = {
  execute: () => void;
  status: "success";
  data: T;
  error: null;
  isLoading: false;
};

type RejectedState = {
  execute: () => void;
  status: "error";
  data: null;
  error: Error;
  isLoading: false;
};

type AsyncState<T> = IdleState | PendingState | ResolvedState<T> | RejectedState;

const useAsync = <Data = any>(asyncFunction: () => Promise<Data>, immediate: boolean = true): AsyncState<Data> => {
  const [status, setStatus] = useState("idle");
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<false | true>(true);
  // The execute function wraps asyncFunction and
  // handles setting state for pending, value, and error.
  // useCallback ensures the below useEffect is not called
  // on every render, but only if asyncFunction changes.
  const execute = useCallback(() => {
    setStatus("pending");
    setData(null);
    setError(null);
    return asyncFunction()
      .then((response) => {
        setData(response);
        setStatus("success");
        setIsLoading(false);
      })
      .catch((error) => {
        setError(error);
        setStatus("error");
        setIsLoading(false);
      });
  }, [asyncFunction]);
  // Call execute if we want to fire it right away.
  // Otherwise execute can be called later, such as
  // in an onClick handler.
  useEffect(() => {
    if (immediate) {
      execute();
    }
  }, [execute, immediate]);
  return {
    execute,
    status,
    data,
    error,
    isLoading,
  } as AsyncState<Data>;
};

export default useAsync;
